import axios from 'axios';
import { setupAxiosInterceptor } from './auth/axiosConfig';
import config from './config';

const humanComputerApi = axios.create({
  baseURL: config.API_BASE_URL + '/cellcog/human-computer',
});

setupAxiosInterceptor(humanComputerApi);

/**
 * Poll for pending commands (Electron app endpoint)
 * @param {Object} systemInfo - System info from the local machine
 */
export const poll = (systemInfo = {}) => {
  return humanComputerApi.get('/poll', { params: systemInfo });
};

/**
 * Submit command execution results (Electron app endpoint)
 * @param {Array} results - Array of command results
 */
export const submitResults = (results) => {
  return humanComputerApi.post('/results', { results });
};

/**
 * Get pending commands for a chat (Web UI)
 * @param {string} chatId - Chat ID
 */
export const getWaitingApprovals = () => humanComputerApi.get('/waiting');

export const getPendingCommands = (chatId) => {
  return humanComputerApi.get(`/${chatId}/pending`);
};

/**
 * Approve a single command
 * @param {string} chatId - Chat ID
 * @param {string} commandId - Command ID
 */
export const approveCommand = (chatId, commandId) => {
  return humanComputerApi.post(`/${chatId}/commands/${commandId}/approve`);
};

/**
 * Deny a single command
 * @param {string} chatId - Chat ID
 * @param {string} commandId - Command ID
 * @param {string} reason - Optional denial reason
 */
export const denyCommand = (chatId, commandId, reason = null) => {
  return humanComputerApi.post(`/${chatId}/commands/${commandId}/deny`, { reason });
};

/**
 * Approve all remaining pending commands
 * @param {string} chatId - Chat ID
 */
export const approveAllCommands = (chatId) => {
  return humanComputerApi.post(`/${chatId}/commands/approve-all`);
};

/**
 * Get HC connection status and settings.
 * @param {string|null} chatId - Optional: scope connection status to the CHAT OWNER's
 *   machine (org-shared employee chats — this chat's commands run on the owner's computer).
 */
export const getStatus = (chatId = null) => {
  return humanComputerApi.get('/status', { params: chatId ? { chat_id: chatId } : undefined });
};

/**
 * CEL-3646: pick which CellCog Desktop runs commands when 2+ are open on this account.
 * Current user only (never chat-scoped). Returns the same shape as getStatus.
 * @param {string} deviceId - a live machine's device_id (from status `machines`)
 */
export const setActiveDevice = (deviceId) => {
  return humanComputerApi.post('/active-device', { device_id: deviceId });
};

/**
 * Update HC terminal auto-approve threshold.
 *
 * @param {Object} settings - { terminal_threat_threshold } where
 *   threshold ∈ {none, safe, moderate, dangerous}.
 *
 * Mirror of `updateBrowserSettings` — same single-threshold shape.
 * The 5-boolean settings shape was replaced in the threat-unification
 * refactor: see py/cellcog/worker/tool_human_computer.py.
 */
export const updateTerminalSettings = (settings) => {
  return humanComputerApi.put('/settings', settings);
};

/**
 * Update per-chat HC configuration
 * @param {string} chatId - Chat ID
 * @param {Object} config - { enabled, working_directory }
 */
export const updateChatConfig = (chatId, config) => {
  return humanComputerApi.put(`/${chatId}/config`, config);
};

// ==================== Browser (Browse my Chrome) ====================

/**
 * Get browser connection status (extension, Chrome, profiles).
 * @param {string|null} chatId - Optional: scope to the CHAT OWNER's Chrome
 *   (org-shared employee chats — Browse commands route to the owner's machine).
 */
export const getBrowserStatus = (chatId = null) => {
  return humanComputerApi.get('/browser/status', { params: chatId ? { chat_id: chatId } : undefined });
};

/**
 * CEL-3671 "Install here": ask the caller's OWN CellCog Desktop to open the CellCog Browse Web Store
 * page in one Chrome profile. Show it only when GET /browser/status says `open_in_profile_supported`.
 * 200 {success, request_id}; refusals carry a plain `detail` to show as is (400 bad key, 404 profile not
 * on this computer, 409 open the desktop / two computers / update it, 503 try again).
 * @param {string} profileKey - a profileDir from `available_profiles`
 */
export const openExtensionInProfile = (profileKey) => {
  return humanComputerApi.post('/browser/open-in-profile', { profile_key: profileKey });
};

/**
 * Get browser auto-approve settings (threat threshold only).
 *
 * Domain allowlist and personal-tab discovery opt-in were retired in
 * PRD v3 — threat level is the single auto-approval gate now.
 */
export const getBrowserSettings = () => {
  return humanComputerApi.get('/browser/settings');
};

/**
 * Update browser auto-approve settings
 * @param {Object} settings - { threat_threshold }
 */
export const updateBrowserSettings = (settings) => {
  return humanComputerApi.put('/browser/settings', settings);
};

export default humanComputerApi;
